import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { resolveHelpContent } from '../../config/helpContent';
import { resolvePageTitleFromPath } from '../../config/pageTitleFromPath';
import PageHelpDrawer from './PageHelpDrawer';
import PageTourOverlay from '../tour/PageTourOverlay';
import { usePageTour } from '../tour/usePageTour';
import { useCurrentPageTour } from '../tour/PageTourContext';
import { usePermissions } from '../../contexts/PermissionContext';

export type PageHelpButtonProps = {
  size?: 'small' | 'medium' | 'large';
};

/**
 * Botón global de ayuda contextual según la ruta actual.
 * Si la pantalla registró un tour (`useRegisterPageTour`), el drawer ofrece «Tour guiado».
 */
const PageHelpButton: React.FC<PageHelpButtonProps> = ({ size = 'medium' }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const content = resolveHelpContent(pathname);
  const pageTitle = resolvePageTitleFromPath(pathname);
  const tourDef = useCurrentPageTour();
  const tourAutoStartedRef = useRef(false);
  const { can } = usePermissions();
  const continueTo =
    tourDef?.continueTo && (!tourDef.continuePermiso || can(tourDef.continuePermiso))
      ? tourDef.continueTo
      : undefined;

  const pageTour = usePageTour({
    steps: tourDef?.steps ?? [],
    onComplete: () => {
      if (continueTo) navigate(continueTo);
    },
  });

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const { start: startTour, close: closeTour } = pageTour;

  // Al cambiar de pantalla se cierra el tour anterior.
  useEffect(() => {
    tourAutoStartedRef.current = false;
    closeTour();
  }, [tourDef?.id, closeTour]);

  // Continuación listado → POS (?tour=1)
  useEffect(() => {
    if (searchParams.get('tour') !== '1' || !tourDef || tourAutoStartedRef.current) return undefined;
    const timer = window.setTimeout(() => {
      tourAutoStartedRef.current = true;
      startTour();
      const next = new URLSearchParams(searchParams);
      next.delete('tour');
      setSearchParams(next, { replace: true });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [searchParams, setSearchParams, tourDef, startTour]);

  return (
    <>
      <Tooltip title="Ayuda de esta pantalla" enterTouchDelay={0}>
        <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex' }}>
          <IconButton
            color="inherit"
            onClick={handleOpen}
            aria-label="Abrir ayuda de esta pantalla"
            aria-haspopup="dialog"
            aria-expanded={open}
            size={size}
            sx={{
              color: '#00548f',
              '&:hover': {
                backgroundColor: 'rgba(0, 84, 143, 0.08)',
              },
            }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Box>
      </Tooltip>
      <PageHelpDrawer
        open={open}
        onClose={handleClose}
        pageTitle={pageTitle}
        content={content}
        tourAvailable={Boolean(tourDef)}
        onStartTour={tourDef ? startTour : undefined}
      />
      <PageTourOverlay
        step={pageTour.currentStep}
        stepOrdinal={pageTour.stepOrdinal}
        stepCount={pageTour.stepCount}
        isFirst={pageTour.isFirst}
        isLast={pageTour.isLast}
        lastLabel={continueTo ? tourDef?.continueLabel : undefined}
        onClose={closeTour}
        onNext={pageTour.next}
        onPrev={pageTour.prev}
      />
    </>
  );
};

export default PageHelpButton;
